import { queryClient } from '@/lib/query-client';
import { createQueryKeys } from '@/lib/query-factory';
import { TicketEvent, TicketWithEvents } from '@certhon/models';
import { MutationOptions, queryOptions } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from './tickets.api';
import { TicketFilter } from '@/features/tickets/tickets.hooks';

const keys = createQueryKeys('tickets');

export const ticketQueries = {
  keys,

  list: (filters?: TicketFilter) =>
    queryOptions({
      queryKey: keys.list(filters),
      queryFn: () => api.fetchTickets(filters),
    }),

  detail: (id: string) =>
    queryOptions({
      queryKey: keys.detail(id),
      queryFn: () => api.fetchTicket(id),
      enabled: !!id,
    }),

  /**
   * `outboundIds` = the subject's ticket_references, taken from the caller's
   * already-loaded ticket (undefined while it loads — query waits).
   */
  referencedTickets: (ticketId: string, outboundIds?: string[]) =>
    queryOptions({
      queryKey: [...keys.lists(), 'referenced', ticketId],
      queryFn: () => api.fetchReferencedTickets(ticketId, outboundIds ?? []),
      enabled: !!ticketId && outboundIds !== undefined,
    }),

  /** Tickets of a klant — callers pass the loaded ticket's client_id. */
  clientTickets: (clientId: number | null | undefined) =>
    queryOptions({
      queryKey: [...keys.lists(), 'client', clientId],
      queryFn: () => api.fetchTickets({ client_id: `eq.${clientId}` }),
      enabled: clientId != null,
    }),

  /**
   * CRM-chain contract: werk-level tickets carry their werk's project as an
   * explicit ancestor, so this single filter covers tickets attached to the
   * project directly AND via one of its works.
   *
   * TODO(offline): the API returns an archived project's tickets for this
   * pinned query, but the sync never stores them (see the SW's
   * eventSourcedTickets header). Offline this list is empty for an archived
   * project — show a notice ("not available offline") instead of an empty
   * table. Same for workTickets and the ticket detail query.
   */
  projectTickets: (projectId: number) =>
    queryOptions({
      queryKey: [...keys.lists(), 'project', projectId],
      queryFn: () => api.fetchTickets({ project_id: `eq.${projectId}` }),
      enabled: !!projectId,
    }),

  workTickets: (workId: number) =>
    queryOptions({
      queryKey: [...keys.lists(), 'work', workId],
      queryFn: () => api.fetchTickets({ werk_id: `eq.${workId}` }),
      enabled: !!workId,
    }),
};

export const ticketMutations = {
  postEvents: (): MutationOptions<
    TicketWithEvents[],
    Error,
    TicketEvent[]
  > => ({
    mutationFn: api.postTicketEvents,
    onSuccess: (tickets) => {
      // Always invalidate all ticket lists since they may contain the updated tickets
      queryClient.invalidateQueries({ queryKey: keys.lists() });

      // invalidate all detail from result
      tickets.forEach(({ id }) => {
        queryClient.invalidateQueries({ queryKey: keys.detail(id) });
      });

      // Invalidate specific list queries that might contain these tickets
      queryClient.invalidateQueries({ queryKey: [...keys.lists(), 'project'] });
      queryClient.invalidateQueries({ queryKey: [...keys.lists(), 'work'] });

      // TODO: These queries are expensive and should eventually be invalidated more sparingly.
      // They only need to be refetched when relevant fields change:
      // - 'referenced': When ticket relationships/references change
      // - 'client': When client/project/work associations change
      // Currently invalidating on all ticket mutations to prevent stale data.
      queryClient.invalidateQueries({
        queryKey: [...keys.lists(), 'referenced'],
      });
      queryClient.invalidateQueries({
        queryKey: [...keys.lists(), 'client'],
      });

      if (tickets.length === 1) {
        toast.success('Ticket updated');
      } else {
        toast.success(`${tickets.length} tickets updated`);
      }
    },
    onError: (error: Error) => {
      toast.error('Ticket update failed', {
        description: error.message,
      });
    },
  }),
};
